import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Connection } from '@domain/connection/models/Connection';
import { SubscriptionPlan } from '@domain/subscription/models/SubscriptionPlan';
import { ClientType } from '@domain/connection/models/Connector';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useConnections } from '@shared/hooks';
import { container } from '@infrastructure/container';
import { SelectShopifyShopStep, shopMatchesDomain } from './SelectShopifyShopStep';
import ShopifyConnectForm from '@features/connections/shopify/components/ShopifyConnectForm';
import { Icon } from '@shared/icons';

interface SelectShopifyShopDialogProps {
  open: boolean;
  plan: SubscriptionPlan | null;
  previouslyBilledShopDomain?: string;
  checkoutError?: string | null;
  onClose?: () => void;
}

export const SelectShopifyShopDialog: React.FC<SelectShopifyShopDialogProps> = ({
  open,
  plan,
  previouslyBilledShopDomain,
  checkoutError,
  onClose,
}) => {
  const { connections } = useConnections();
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reconnectConnectionId, setReconnectConnectionId] = useState<string | undefined>();
  const [isAddingConnection, setIsAddingConnection] = useState(false);

  const shops = useMemo(
    () => connections?.filter((connection: Connection) => connection.connector.type === ClientType.SHOPIFY) ?? [],
    [connections],
  );
  const authorizedShops = useMemo(
    () => shops.filter(shop => shop.authorized),
    [shops],
  );
  const selectedShop = shops.find(shop => shop.id === selectedConnectionId);
  const isConnecting = Boolean(reconnectConnectionId) || isAddingConnection;

  useEffect(() => {
    if (!open) return;
    setIsSubmitting(false);
    setReconnectConnectionId(undefined);
    setIsAddingConnection(false);
    setError(checkoutError ?? null);
  }, [open, checkoutError]);

  useEffect(() => {
    if (!open || selectedConnectionId || authorizedShops.length === 0) return;
    const previouslyBilled = authorizedShops.find(shop =>
      shopMatchesDomain(shop, previouslyBilledShopDomain),
    );
    setSelectedConnectionId(previouslyBilled?.id ?? authorizedShops[0]?.id);
  }, [open, selectedConnectionId, authorizedShops, previouslyBilledShopDomain]);

  const handleDismissOverlay = () => {
    setReconnectConnectionId(undefined);
    setIsAddingConnection(false);
  };

  const handleClose = () => {
    if (isConnecting) {
      handleDismissOverlay();
      return;
    }
    if (!onClose) return;
    onClose();
  };

  const handleContinue = async () => {
    if (isSubmitting || !plan || !selectedShop?.authorized) return;
    setIsSubmitting(true);
    setError(null);
    const result = await container.subscriptionRepository.startShopifyCheckout(plan.id, selectedShop.id);
    if (result.status === 'success' && result.data?.confirmationUrl) {
      window.location.href = result.data.confirmationUrl;
      return;
    }
    setError(result.status === 'error'
      ? result.error?.message ?? 'Could not start Shopify checkout'
      : 'Could not start Shopify checkout');
    setIsSubmitting(false);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (isConnecting) {
            handleDismissOverlay();
            return;
          }
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      {isConnecting ? (
        <ShopifyConnectForm
          connectionId={isAddingConnection ? undefined : reconnectConnectionId}
          onClose={handleDismissOverlay}
        />
      ) : (
        <>
          <DialogHeader>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" fontWeight={600}>
                  Select a shop
                </Typography>
              </Stack>
              {onClose && (
                <IconButton onClick={handleClose} size="small">
                  <Icon icon="X01" />
                </IconButton>
              )}
            </Box>
          </DialogHeader>

          <DialogContent sx={{ p: 3 }}>
            {connections === undefined ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                {error && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}
                <SelectShopifyShopStep
                  shops={shops}
                  selectedConnectionId={selectedConnectionId}
                  previouslyBilledShopDomain={previouslyBilledShopDomain}
                  onSelect={setSelectedConnectionId}
                  onContinue={() => { void handleContinue(); }}
                  onAddConnection={() => setIsAddingConnection(true)}
                  onReconnect={setReconnectConnectionId}
                />
              </Stack>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};
